import Axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { RetryLink } from '@apollo/client/link/retry';
import { ApolloClient, HttpLink, InMemoryCache, gql, ApolloQueryResult, FetchResult, ApolloLink, QueryOptions } from '@apollo/client/core';
import Cookies from 'js-cookie';
import { Key } from '@/types/constants/Key';

/* eslint-disable @typescript-eslint/no-explicit-any*/

const retryLink = new RetryLink({
  attempts: { max: 10 },
  delay: { initial: 300, max: 2000, jitter: true },
});

const httpLink = new HttpLink({
  uri: import.meta.env.VITE_BACKEND_URL,
});

const authMiddleware = new ApolloLink((operation, forward) => {
  // add the authorization to the headers
  const token = localStorage.getItem('token');
  operation.setContext({
    headers: {
      authorization: token ? `Bearer ${token}` : '',
    },
  });
  return forward(operation);
});

const link = ApolloLink.from([retryLink, authMiddleware, httpLink]);

//New für caching only 1 instance  of ApolloClient needed and used everywhere
export const apolloClient = new ApolloClient({
  link: link,
  cache: new InMemoryCache(),
});

/*const getUncachedApolloClient = (): ApolloClient<NormalizedCacheObject> => {
  return new ApolloClient({
    link: concat(authMiddleware, httpLink),
    cache: new InMemoryCache(),
  });
};*/

export const query = async (
  queryString: string,
  options: Omit<QueryOptions, 'query'> = {}
): Promise<ApolloQueryResult<any>> => {

  return await apolloClient.query({
    query: gql(queryString),
    ...options

  });
};

export const queryWithVariables = async (
  query: string,
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  variables: any,
  abortController: AbortController = new AbortController(),
  shouldRefetch?: boolean
): Promise<FetchResult<any>> => {
  //const apolloClient = getUncachedApolloClient();
  return await apolloClient.query({
    query: gql(query),
    variables: variables,
    context: {
      fetchOptions: {
        signal: abortController.signal,
      },
    },
    fetchPolicy: shouldRefetch ? 'network-only' : 'cache-first', // New Use cache-first policy by default
  });
};

export const mutate = async (mutation: string): Promise<FetchResult<any>> => {
  //const apolloClient = getUncachedApolloClient();
  return await apolloClient.mutate({
    mutation: gql(mutation),
  });
};

export const mutateWithVariables = async (
  mutation: string,
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  variables: any,
  abortController: AbortController = new AbortController()
): Promise<FetchResult<any>> => {
  //const apolloClient = getUncachedApolloClient();
  return await apolloClient.mutate({
    mutation: gql(mutation),
    variables: variables,
    context: {
      fetchOptions: {
        signal: abortController.signal,
      },
    },
  });
};

export const endpoint = (baseURL = `${import.meta.env.VITE_API_PATH}`, options?: Partial<AxiosRequestConfig>): AxiosInstance => {
  const config = {
    baseURL: baseURL,
    headers: {
      'accept-language': 'de-AT,de-DE;q=0.9,de;q=0.8,en-US;q=0.7,en;q=0.6',
    },
    ...options,
  };
  if (!config.baseURL.endsWith('/')) {
    config.baseURL = `${config.baseURL}/`;
  }
  const axiosInstance = Axios.create(config);

  // response interceptors are used to catch errors globally to show a generic error message
  axiosInstance.interceptors.response.use(
    (response) => {
      /* add any response interceptors if desired */
      return response;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  return axiosInstance;
};

export const endpointAuthSystem = (baseURL = `${import.meta.env.VITE_AUTH_SYSTEM_LARAVEL_URL}`): AxiosInstance => {
  const axiosInstance: AxiosInstance = Axios.create({
    baseURL: baseURL,
    timeout: 30000, // 30 seconds
  });

  axiosInstance.interceptors.request.use(
    (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
      const newConfig = { ...config };
      newConfig.headers = config.headers || {};
      const token = Cookies.get(Key.cookie.access_token_key);

      // TODO: Adjust token authorization
      if (token && newConfig.headers) newConfig.headers.Authorization = token;

      return newConfig;
    },
    (error) => {
      console.error('[Request Error]:', error);
      return Promise.reject(error);
    }
  );
  // Response interceptor
  axiosInstance.interceptors.response.use(
    (response: AxiosResponse): AxiosResponse => {
      return response;
    },
    (error) => {
      if (error.response) console.error(`[Response Error ${error.response.status}]:`, error.response.data);
      else if (error.request) console.error('[Network Error]:', error.request);
      else console.error('[Error]:', error.message);
      return Promise.reject(error);
    }
  );

  return axiosInstance;
};

export async function apiExecuteGet<T = any>(url: string): Promise<T> {
  const baseUrl = window.location.origin;
  const { data: result } = await endpoint(baseUrl).get<T>(url);
  return result;
}

export async function apiExecutePost<T = any>(
  url: string,
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  body: any,
  baseUrl = window.location.origin,
  options?: Partial<AxiosRequestConfig>
): Promise<T> {
  //const baseUrl = window.location.origin;
  const { data: result } = await endpoint(baseUrl, options).post<T>(url, body);
  return result;
}

export const apiExecutePostWithProgress = async (
  url: string,
  body: any,
  baseUrl: string = window.location.origin,
  options?: Partial<AxiosRequestConfig>
) => {
  return await endpoint(baseUrl, {
    ...options,
    onUploadProgress: options?.onUploadProgress,
  }).post(url, body);
};
